import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  List,
  Progress,
  Row,
  Skeleton,
  Tag,
  Typography
} from "antd";
import {
  CalendarOutlined,
  EditOutlined,
  EuroOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  RightOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { getOverview, confirmSlot, errorText } from "./api";
import { formatDay, formatEuro } from "../../../serviceProvider/components/workReports/format";
import { documentKind, paymentState, projectStatus } from "./labels";
import { slotText, appointmentText } from "./time";
import "../../../serviceProvider/components/dashboard/Dashboard.css";
import "./portal.css";

const { Text } = Typography;

/** Customer home: what needs an answer, what is due, what is next, what happened. */
const PortalOverview = () => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const [state, setState] = useState({ loading: true });
  const [busySlot, setBusySlot] = useState("");
  const [notice, setNotice] = useState(null);

  const load = useCallback(() => {
    getOverview()
      .then(res => setState({ loading: false, data: res.data.data }))
      .catch(error =>
        setState({
          loading: false,
          error: errorText(error, t("The overview could not be loaded."))
        })
      );
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const confirm = async (appointment, slot) => {
    setBusySlot(`${appointment._id}:${slot.index}`);
    setNotice(null);
    try {
      await confirmSlot(appointment._id, slot.index);
      setNotice({ type: "success", text: t("Thank you, the appointment is confirmed.") });
      load();
    } catch (error) {
      setNotice({
        type: "error",
        text: errorText(error, t("This time is no longer available. Please choose another one."))
      });
    } finally {
      setBusySlot("");
    }
  };

  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? t("Good morning") : hour < 18 ? t("Good afternoon") : t("Good evening");

  if (state.loading) {
    return (
      <div className="portal-page">
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="portal-page">
        <Alert type="error" showIcon message={state.error} />
      </div>
    );
  }

  const d = state.data;
  const todo = [
    ...d.offersToDecide.map(o => ({
      key: `o${o._id}`,
      to: `/client/offers/${o._id}`,
      icon: <FileTextOutlined />,
      title: `${t("Offer")} ${o.number}`,
      text: o.title,
      amount: formatEuro(o.totals.gross, lng)
    })),
    ...d.changeOrdersToDecide.map(co => ({
      key: `c${co._id}`,
      to: `/client/offers/change-orders/${co._id}`,
      icon: <EditOutlined />,
      title: `${t("Change order")} ${co.number}`,
      text: co.title,
      amount: formatEuro(co.totals.gross, lng)
    }))
  ];

  return (
    <div className="portal-page">
      <section className="portal-hero">
        {d.company.logo ? <img className="portal-hero__logo" src={d.company.logo} alt="" /> : null}
        <div>
          <h2>
            {greeting}, {d.customer.firstName}
          </h2>
          <p>
            {t("Your portal at {{company}}: offers, invoices, appointments and documents.", {
              company: d.company.name
            })}
          </p>
        </div>
      </section>

      {notice && (
        <Alert className="portal-gap" type={notice.type} showIcon message={notice.text} closable />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title={t("Waiting for your answer")} className="portal-card">
            {todo.length ? (
              <List
                dataSource={todo}
                renderItem={item => (
                  <List.Item>
                    <Link className="portal-row" to={item.to}>
                      <span className="portal-row__icon">{item.icon}</span>
                      <span className="portal-row__main">
                        <strong>{item.title}</strong>
                        <Text type="secondary">{item.text}</Text>
                      </span>
                      <span className="portal-row__end">
                        <strong>{item.amount}</strong>
                        <RightOutlined className="portal-chevron" aria-hidden="true" />
                      </span>
                    </Link>
                  </List.Item>
                )}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("Nothing to decide right now.")}
              />
            )}
            {d.signatureRequests.length > 0 && (
              <List
                className="portal-gap-top"
                header={<Text strong>{t("Waiting for your signature")}</Text>}
                dataSource={d.signatureRequests}
                renderItem={s => (
                  <List.Item>
                    <span className="portal-row">
                      <span className="portal-row__icon">
                        <FileDoneOutlined />
                      </span>
                      <span className="portal-row__main">
                        <strong>{s.title}</strong>
                        <Text type="secondary">
                          {t("We sent you a link to sign by email to {{email}}.", {
                            email: s.sentTo || "–"
                          })}
                        </Text>
                      </span>
                    </span>
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={t("Open invoices")}
            className="portal-card"
            extra={<Link to="/client/invoices">{t("All invoices")}</Link>}
          >
            {d.openInvoices.length ? (
              <>
                <div className="portal-total">
                  <EuroOutlined aria-hidden="true" />
                  <span>{t("Total due")}</span>
                  <strong>{formatEuro(d.openTotalCents, lng)}</strong>
                </div>
                <List
                  dataSource={d.openInvoices}
                  renderItem={inv => {
                    const tag = paymentState(t, inv.paymentState);
                    return (
                      <List.Item>
                        <Link className="portal-row" to={`/client/invoices/${inv._id}`}>
                          <span className="portal-row__main">
                            <strong>{inv.number}</strong>
                            <Text type="secondary">
                              {inv.dueDate
                                ? t("Due on {{date}}", { date: formatDay(inv.dueDate) })
                                : inv.title}
                            </Text>
                          </span>
                          <span className="portal-row__end">
                            <Tag color={tag.color}>{tag.text}</Tag>
                            <strong>{formatEuro(inv.openCents, lng)}</strong>
                          </span>
                        </Link>
                      </List.Item>
                    );
                  }}
                />
              </>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("No open invoices.")} />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={t("Appointments")}
            className="portal-card"
            extra={<Link to="/client/appointments">{t("Calendar")}</Link>}
          >
            {d.appointments.length ? (
              <List
                dataSource={d.appointments}
                renderItem={a => (
                  <List.Item>
                    <div className="portal-appointment">
                      <div className="portal-row">
                        <span className="portal-row__icon">
                          <CalendarOutlined />
                        </span>
                        <span className="portal-row__main">
                          <strong>{a.title}</strong>
                          <Text type="secondary">
                            {a.proposal
                              ? t("Please choose one of the proposed times.")
                              : appointmentText(a, lng)}
                          </Text>
                          {a.staff.length > 0 && <Text type="secondary">{a.staff.join(", ")}</Text>}
                        </span>
                      </div>
                      {a.proposal && (
                        <div className="portal-slots">
                          {a.slots.map(slot => (
                            <Button
                              key={slot.index}
                              block
                              loading={busySlot === `${a._id}:${slot.index}`}
                              disabled={Boolean(busySlot)}
                              onClick={() => confirm(a, slot)}
                            >
                              {slotText(slot, lng)}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  </List.Item>
                )}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("No upcoming appointments.")}
              />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={t("Your projects")}
            className="portal-card"
            extra={<Link to="/client/documents">{t("Documents")}</Link>}
          >
            {d.projects.length ? (
              <List
                dataSource={d.projects}
                renderItem={p => (
                  <List.Item>
                    <div className="portal-project">
                      <div className="portal-project__head">
                        <strong>{p.name}</strong>
                        <Tag>{projectStatus(t, p.status)}</Tag>
                      </div>
                      <Progress percent={Math.min(100, Math.max(0, p.progress))} size="small" />
                    </div>
                  </List.Item>
                )}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("No projects yet.")} />
            )}
            {d.recentDocuments.length > 0 && (
              <List
                className="portal-gap-top"
                header={<Text strong>{t("New documents")}</Text>}
                dataSource={d.recentDocuments}
                renderItem={doc => (
                  <List.Item>
                    <Link className="portal-row" to="/client/documents">
                      <span className="portal-row__main">
                        <strong>{doc.title}</strong>
                        <Text type="secondary">
                          {[documentKind(t, doc.kind), formatDay(doc.date)]
                            .filter(Boolean)
                            .join(" · ")}
                        </Text>
                      </span>
                    </Link>
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>

        {d.activity.length > 0 && (
          <Col xs={24}>
            <Card title={t("Recent activity")} className="portal-card">
              <List
                dataSource={d.activity}
                renderItem={item => (
                  <List.Item>
                    <Link className="portal-row" to={item.link}>
                      <span className="portal-row__main">
                        <strong>{item.title}</strong>
                        <Text type="secondary">
                          {[
                            activityLabel(t, item.type),
                            formatDay(String(item.at || "").slice(0, 10))
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </Text>
                      </span>
                    </Link>
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
};

const activityLabel = (t, type) =>
  ({
    offer_received: t("New offer"),
    offer_accepted: t("Offer accepted"),
    offer_declined: t("Offer declined"),
    invoice: t("Invoice"),
    document: t("Document"),
    change_order: t("Change order")
  })[type] || "";

export default PortalOverview;
